// src/redux/Client/vulnerability.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiServices from "../../service";
import type { AppDispatch } from "../../redux/store";

interface VulnerabilityState {
  listItems: any[];
  activeItem: any;
  loading: boolean;
  activeItemloading: boolean;
}

const initialState: VulnerabilityState = {
  listItems: [],
  activeItem: {},
  loading: false,
  activeItemloading: false,
};

/* -------------------------------------------------------------------------- */
/*  1. LIST – fetch all vulnerability records for a client                    */
/* -------------------------------------------------------------------------- */
export const getVulnerabilityListItems = createAsyncThunk(
  "vulnerability/getVulnerabilityListItems",
  async (clientId: string, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.client.vulnerability.get(
        clientId
      );
      const { isError, data } = response.data;
      if (isError || !data) {
        return rejectWithValue("Failed to fetch vulnerability list");
      }
      return data; // <-- array of records
    } catch (error: any) {
      return rejectWithValue(error.message ?? "An error occurred");
    }
  }
);

/* -------------------------------------------------------------------------- */
/*  2. DETAIL – pick the record from the already-loaded list                 */
/* -------------------------------------------------------------------------- */
export const getVulnerabilityDetailById = createAsyncThunk(
  "vulnerability/getVulnerabilityDetailById",
  async (
    vulnerabilityId: string,
    { getState, dispatch, rejectWithValue }
  ): Promise<any> => {
    if (!vulnerabilityId) return rejectWithValue("Invalid vulnerability ID");

    const state = getState() as any;
    const list = state.vulnerability.listItems as any[];

    const item = list.find((i) => i.vulnerability_id === vulnerabilityId);
    if (item) {
      return item; // found → return it
    }

    // NOT found → **clear the stale activeItem** and reject
    (dispatch as AppDispatch)(clearActiveItem());
    return rejectWithValue("Vulnerability record not found in current client");
  }
);

/* -------------------------------------------------------------------------- */
/*  3. SLICE                                                                 */
/* -------------------------------------------------------------------------- */
export const vulnerabilitySlice = createSlice({
  name: "vulnerability",
  initialState,
  reducers: {
    /** Clears everything when we switch client */
    clearList: (state) => {
      state.listItems = [];
      state.activeItem = {};
      state.loading = false;
      state.activeItemloading = false;
    },

    /** Clears only the selected record (used by the thunk) */
    clearActiveItem: (state) => {
      state.activeItem = {};
      state.activeItemloading = false;
    },
  },

  extraReducers: (builder) => {
    /* ---------- LIST ---------- */
    builder
      .addCase(getVulnerabilityListItems.pending, (state) => {
        state.loading = true;
        state.listItems = [];
      })
      .addCase(
        getVulnerabilityListItems.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.loading = false;
          state.listItems = action.payload;
        }
      )
      .addCase(getVulnerabilityListItems.rejected, (state) => {
        state.loading = false;
      });

    /* ---------- DETAIL ---------- */
    builder
      .addCase(getVulnerabilityDetailById.pending, (state) => {
        state.activeItemloading = true;
        state.activeItem = {};
      })
      .addCase(
        getVulnerabilityDetailById.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.activeItemloading = false;
          state.activeItem = action.payload;
        }
      )
      .addCase(getVulnerabilityDetailById.rejected, (state) => {
        state.activeItemloading = false;
        // activeItem already cleared by the thunk
      });
  },
});

export const { clearList, clearActiveItem } = vulnerabilitySlice.actions;
export default vulnerabilitySlice.reducer;
